import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { payoutsAPI, leadsAPI, reportsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Loader2,
  DollarSign,
  Filter,
  MoreHorizontal,
  CheckCircle,
  Clock,
  XCircle,
  User,
  Heart,
  FileDown,
} from 'lucide-react';
import { toast } from 'sonner';

const Payouts = () => {
  const { user, isAdmin, canManagePipeline } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);
  const [exportingPDF, setExportingPDF] = useState(false);

  const loadPayouts = useCallback(async () => {
    try {
      const response = await payoutsAPI.getAll();
      setPayouts(response.data);
    } catch (error) {
      toast.error('Failed to load payouts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPayouts();
  }, [loadPayouts]);

  const handleUpdateStatus = async (payoutId, newStatus) => {
    setUpdatingId(payoutId);
    try {
      await payoutsAPI.updateStatus(payoutId, newStatus);
      setPayouts(prev => prev.map(p => 
        p.id === payoutId ? { ...p, status: newStatus } : p
      ));
      toast.success('Payout status updated');
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleExportMyPDF = async () => {
    if (!user?.id) return;
    setExportingPDF(true);
    try {
      const response = await reportsAPI.exportRepPDF(user.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `commission_statement_${user.name?.replace(/\s+/g, '_') || 'my'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Commission statement downloaded');
    } catch (error) {
      toast.error('Failed to export commission statement');
    } finally {
      setExportingPDF(false);
    }
  };

  const filteredPayouts = payouts.filter((payout) => {
    const matchesStatus = statusFilter === 'all' || payout.status === statusFilter;
    const matchesType = typeFilter === 'all' || payout.payee_type === typeFilter;
    return matchesStatus && matchesType;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Paid':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'Due':
        return <Clock className="w-4 h-4 text-amber-500" />;
      case 'Voided':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Paid': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      'Due': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      'Not Due': 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
      'Voided': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    };
    return styles[status] || styles['Not Due'];
  };

  // Calculate totals
  const totals = {
    totalDue: filteredPayouts.filter(p => p.status === 'Due').reduce((sum, p) => sum + p.amount, 0),
    totalPaid: filteredPayouts.filter(p => p.status === 'Paid').reduce((sum, p) => sum + p.amount, 0),
    repDue: filteredPayouts.filter(p => p.status === 'Due' && p.payee_type === 'SalesRep').reduce((sum, p) => sum + p.amount, 0),
    nonprofitDue: filteredPayouts.filter(p => p.status === 'Due' && p.payee_type === 'Nonprofit').reduce((sum, p) => sum + p.amount, 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payouts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
            Payouts
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            {user?.role === 'SalesRep' ? 'Track your commissions' : 'Manage commissions and donations'}
          </p>
        </div>
        {user?.role === 'SalesRep' && (
          <Button
            onClick={handleExportMyPDF}
            disabled={exportingPDF}
            variant="outline"
            className="border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20"
            data-testid="export-my-statement-btn"
          >
            {exportingPDF ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FileDown className="w-4 h-4 mr-2 text-red-500" />
                Download Statement (PDF)
              </>
            )}
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="stats-card" data-testid="total-due-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Total Due
                </p>
                <p className="text-3xl font-bold font-heading font-mono text-amber-600 mt-2">
                  ${totals.totalDue.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card" data-testid="total-paid-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Total Paid
                </p>
                <p className="text-3xl font-bold font-heading font-mono text-emerald-600 mt-2">
                  ${totals.totalPaid.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card" data-testid="rep-due-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Rep Commissions Due
                </p>
                <p className="text-3xl font-bold font-heading font-mono text-slate-900 dark:text-white mt-2">
                  ${totals.repDue.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                <User className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card" data-testid="nonprofit-due-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Nonprofit Donations Due
                </p>
                <p className="text-3xl font-bold font-heading font-mono text-rose-600 mt-2">
                  ${totals.nonprofitDue.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-rose-100 dark:bg-rose-900/30 rounded-xl">
                <Heart className="w-6 h-6 text-rose-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Not Due">Not Due</SelectItem>
                <SelectItem value="Due">Due</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="Voided">Voided</SelectItem>
              </SelectContent>
            </Select>
            {(isAdmin() || user?.role === 'BackOffice') && (
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="type-filter">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="SalesRep">Sales Rep</SelectItem>
                  <SelectItem value="Nonprofit">Nonprofit</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Payouts Table */}
      <Card>
        <CardContent className="p-0">
          {filteredPayouts.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                    <TableHead>Payee</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Deal</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Due Date</TableHead>
                    {canManagePipeline() && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPayouts.map((payout) => (
                    <TableRow key={payout.id} data-testid={`payout-row-${payout.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {payout.payee_type === 'Nonprofit' ? (
                            <Heart className="w-4 h-4 text-rose-500" />
                          ) : (
                            <User className="w-4 h-4 text-blue-500" />
                          )}
                          <span className="font-medium">
                            {payout.payee_name || 'Unknown'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {payout.payee_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-500 font-mono">
                          {payout.deal_id.slice(0, 8)}...
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">
                          {payout.trigger_milestone}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono font-medium text-emerald-600">
                          ${payout.amount.toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadge(payout.status)}>
                          <span className="flex items-center gap-1">
                            {getStatusIcon(payout.status)}
                            {payout.status}
                          </span>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-500">
                          {new Date(payout.due_date).toLocaleDateString()}
                        </span>
                      </TableCell>
                      {canManagePipeline() && (
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={updatingId === payout.id}
                                data-testid={`payout-actions-${payout.id}`}
                              >
                                {updatingId === payout.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <MoreHorizontal className="w-4 h-4" />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleUpdateStatus(payout.id, 'Due')}
                                disabled={payout.status === 'Due'}
                              >
                                <Clock className="w-4 h-4 mr-2 text-amber-500" />
                                Mark as Due
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleUpdateStatus(payout.id, 'Paid')}
                                disabled={payout.status === 'Paid'}
                              >
                                <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
                                Mark as Paid
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleUpdateStatus(payout.id, 'Voided')}
                                disabled={payout.status === 'Voided'}
                                className="text-red-600"
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Void Payout
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-16">
              <DollarSign className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                No payouts found
              </h3>
              <p className="text-slate-500 dark:text-slate-400">
                Payouts will appear here when deals reach the Installation Completed milestone.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Payouts;
